import { useState } from "react";
import { useThumbnails } from "../../hooks/useThumbnails";
import { useInView } from "../../hooks/useInView";
import { useVideoUrl } from "../../hooks/useVideoUrl";
import { S3Tags } from "./types";
import VideoDebugInfo from "./VideoDebug";
import PublicShareControl from "./PublicShareControl";
import VideoThumbnailCarousel from "./VideoThumbnails";
import { AuthContextProps } from "react-oidc-context";
import { AuthService } from "../../services/auth";
import { PlayIcon, DownloadIcon, WarnIcon } from "../icons";
import { useI18n } from "../../i18n";

type VideoItemProps = {
  title: string;
  tags: S3Tags[];
  dates?: string[];
  onWatchVideo: (videoUrl: string, videoKey: string) => void;
  auth: AuthContextProps;
};

function VideoItem({ title, tags, dates, onWatchVideo, auth }: VideoItemProps) {
  const { t } = useI18n();
  const [cardRef, inView] = useInView<HTMLElement>();
  const { thumbnails, isLoading: thumbnailsLoading } = useThumbnails(title, {
    enabled: inView,
  });
  const {
    fetchVideoUrl,
    isLoading: urlLoading,
    error: urlError,
  } = useVideoUrl();
  const [isWatchLoading, setIsWatchLoading] = useState(false);
  const [isDownloadLoading, setIsDownloadLoading] = useState(false);

  const isAdmin = AuthService.isAdmin(auth);

  const handleWatchClick = async (e: React.MouseEvent) => {
    e.preventDefault();
    setIsWatchLoading(true);

    try {
      const videoUrl = await fetchVideoUrl(title);
      if (videoUrl) {
        onWatchVideo(videoUrl, title);
      }
    } catch (err) {
      console.error("Failed to fetch video URL for watching:", err);
    } finally {
      setIsWatchLoading(false);
    }
  };

  const handleDownloadClick = async (e: React.MouseEvent) => {
    e.preventDefault();
    setIsDownloadLoading(true);

    try {
      const videoUrl = await fetchVideoUrl(title);
      if (videoUrl) {
        const link = document.createElement("a");
        link.href = videoUrl;
        link.target = "_blank";
        link.download = title;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (err) {
      console.error("Failed to fetch video URL for download:", err);
    } finally {
      setIsDownloadLoading(false);
    }
  };

  return (
    <article
      ref={cardRef}
      className="card bg-base-100 shadow-sm overflow-hidden group hover:shadow-md transition-shadow duration-200"
    >
      <VideoThumbnailCarousel
        isLoading={thumbnailsLoading}
        thumbnails={thumbnails}
      />

      <div className="card-body p-3 gap-2">
        <div>
          <h3 className="font-semibold leading-snug">{title}</h3>
          {dates && dates.length > 0 && (
            <p className="text-xs opacity-60 mt-0.5">{dates.join(" · ")}</p>
          )}
        </div>

        {urlError && (
          <div className="alert alert-error alert-sm">
            <WarnIcon className="stroke-current shrink-0 w-4 h-4" />
            <span className="text-xs">{t("videoItem.failed")}</span>
          </div>
        )}

        <div className="card-actions flex gap-2 mt-1">
          <button
            onClick={handleWatchClick}
            disabled={isWatchLoading || urlLoading}
            className="btn btn-primary btn-sm flex-1 flex items-center justify-center gap-2"
          >
            {isWatchLoading || urlLoading ? (
              <span className="loading loading-spinner loading-sm"></span>
            ) : (
              <PlayIcon />
            )}
            {isWatchLoading ? t("common.loading") : t("videoItem.watch")}
          </button>

          <button
            onClick={handleDownloadClick}
            disabled={isDownloadLoading || urlLoading}
            className="btn btn-ghost btn-sm text-base-content/60 hover:text-base-content"
            aria-label={t("videoItem.download")}
            title={t("videoItem.download")}
          >
            {isDownloadLoading ? (
              <span className="loading loading-spinner loading-sm"></span>
            ) : (
              <DownloadIcon />
            )}
          </button>
        </div>

        {isAdmin && <PublicShareControl videoKey={title} tags={tags} />}

        {isAdmin && VideoDebugInfo({ dates, tags, thumbnails, title })}
      </div>
    </article>
  );
}

export default VideoItem;
